// @flow
/**
 * useOnboardingRedirect Hook
 *
 * A React hook that automatically redirects users to the correct onboarding step.
 * Use this hook in onboarding components to ensure users are on the right page.
 *
 * Usage:
 * ```javascript
 * const MyOnboardingComponent = () => {
 *   useOnboardingRedirect();
 *   // ... rest of component
 * };
 * ```
 */

import { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUser } from '../reduxStore/modules/user/selectors';
import { selectBillingState } from '../reduxStore/modules/billing/selectors';
import { selectDefaultBuilding } from '../reduxStore/modules/buildings/selectors';
import { selectIsAppLoading } from '../reduxStore/modules/common/selectors';
import { getOnboardingRedirect } from '../utils/onboardingUtils';
import { log } from '../utils/jsUtils';

/**
 * Hook that automatically redirects user to correct onboarding step.
 * Should be called at the top of onboarding components.
 *
 * The hook checks:
 * 1. If user is trying to access a step they haven't reached yet → redirect to required step
 * 2. If user is on an onboarding page but has completed all steps → redirect to /member
 * 3. If user is on the correct step → no redirect
 */
export const useOnboardingRedirect = (): void => {
  const history = useHistory();
  const location = useLocation();
  const user = useSelector(selectUser);
  const billing = useSelector(selectBillingState);
  const defaultBuilding = useSelector(selectDefaultBuilding);
  const isAppLoading = useSelector(selectIsAppLoading);

  useEffect(() => {
    // Wait for user data AND profile to be fully loaded before checking redirects
    // isAppLoading is true while fetchUserProfile is running in SquarePlanApp
    // This prevents redirecting before IsPhoneVerified and other profile data is set
    if (!user || isAppLoading) {
      log('[useOnboardingRedirect] Waiting for user data to load...');
      return;
    }

    const redirect = getOnboardingRedirect(location.pathname, {
      user,
      billing,
      defaultBuilding
    });

    if (redirect) {
      log('[useOnboardingRedirect] Redirecting from', location.pathname, 'to', redirect);
      history.push(redirect);
    }
  }, [user, billing, defaultBuilding, location.pathname, history, isAppLoading]);
};

export default useOnboardingRedirect;
